import { FileType } from "@/types/others";

// Enums
export enum PERMISSION_KEY {
    ORDER_HISTORY_READ = 'locationOrderHistoryRead',
    LIST_READ = 'locationListRead',
    LIST_ITEM_READ = 'locationListItemRead',
    LIST_ITEM_CREATE = 'locationListItemCreate',
    LIST_ITEM_UPDATE = 'locationListItemUpdate',
    LIST_ITEM_DELETE = 'locationListItemDelete',
    LIST_CREATE = 'locationListCreate',
    LIST_UPDATE = 'locationListUpdate',
    LIST_DELETE = 'locationListDelete',
    LIST_REMINDER_READ = 'locationListReminderRead',
    LIST_REMINDER_CREATE = 'locationListReminderCreate',
    LIST_REMINDER_UPDATE = 'locationListReminderUpdate',
    LIST_REMINDER_DELETE = 'locationListReminderDelete',
    CART_READ = 'locationCartRead',
    CART_ADD = 'locationCartAdd',
    CART_UPDATE = 'locationCartUpdate',
    CART_DELETE = 'locationCartDelete',
    ORDER_CREATE = 'locationOrderCreate',
    ORDER_REQUEST = 'locationOrderRequest',
    LOCATION_READ = 'locationRead',
    LOCATION_CREATE = 'locationCreate',
    LOCATION_UPDATE = 'locationUpdate',
    LOCATION_DELETE = 'locationDelete',
    TEAM_READ = 'locationTeamRead',
    TEAM_MEMBER_ADD = 'locationTeamMemberAdd',
    TEAM_MEMBER_DELETE = 'locationTeamMemberDelete',
    TEAM_MEMBER_ROLE_UPDATE = 'locationTeamMemberRoleUpdate',
    SUPPLIER_READ = 'locationSupplierRead',
    SUPPLIER_CREATE = 'locationSupplierCreate',
    SUPPLIER_DELETE = 'locationSupplierDelete',
    SUPPLIER_CONTACT = 'locationContactSupplier',
    ME_READ = 'meRead',
    ME_UPDATE = 'meUpdate',
    ME_DELETE = 'meDelete'
}

// Interfaces
export interface NotificationPermElementInterface {
    push: boolean;
    email: boolean;
    popup: boolean
}

export interface NotificationPermElement {
    general: NotificationPermSubElement;

    [key: string]: NotificationPermSubElement;
}

export type NotificationKeys = 'order' | 'promotion' | 'news' | 'reminder' | 'productPromotion'

export interface NotificationPermInterface extends Record<NotificationKeys, NotificationPermElement> {
    all: boolean;
}

export interface UserData {
    id: string;
    email: string;
    emailConfirmed: boolean;
    firstName: string;
    lastName: string;
    phone: string;
    city: string;
    postalCode: string;
    street: string;
    streetNumber: string;
    profilePicture?: FileType;
    profilePictureId?: string | null;
}

export interface AccountCredentials {
    email?: string,
    password?: string,
    passwordRepeat?: string,
    firstName: string,
    lastName: string,
    phone?: string,
    street?: string,
    streetNumber?: string,
    postalCode?: string,
    city?: string,
    profilePicture?: FileType,
    profilePictureId?: string | null
}

// Classes
class RegisterCredentials implements AccountCredentials {
    constructor(
        public email: string = '',
        public firstName: string = '',
        public lastName: string = '',
        public profilePicture?: FileType,
    ) {
    }
}

class MeResponse extends RegisterCredentials {
    constructor(
        public id: string = '',
        public emailConfirmed: boolean = false,
        public sessionToken: string = '',
        public firebaseToken?: {
            deviceId: string,
            firebaseToken: string
        },
        public notificationSettings: NotificationPerm = new NotificationPerm(),
        public imageLink = '',
        public liveUpdateChannel?: string,
        public phone?: string,
        public callBot?: {
            language?: string,
            phone?: string
        },
    ) {
        super();
    }
}

export class NotificationPermSubElement implements NotificationPermElementInterface {
    constructor(
        public push = true,
        public email = true,
        public popup = true
    ) {
    }
}

class NotificationPerm implements NotificationPermInterface {
    constructor(
        public all = true,
        public order: NotificationPermElement = {
            general: new NotificationPermSubElement()
        },
        public reminder: NotificationPermElement = {
            general: new NotificationPermSubElement()
        },
        public promotion: NotificationPermElement = {
            general: new NotificationPermSubElement()
        },
        public productPromotion: NotificationPermElement = {
            general: new NotificationPermSubElement()
        },
        public news: NotificationPermElement = {
            general: new NotificationPermSubElement()
        }
    ) {
    }
}

class UserPermissions implements Record<PERMISSION_KEY, boolean> {
    public locationOrderHistoryRead: boolean;
    public locationListRead: boolean;
    public locationListItemRead: boolean;
    public locationListItemCreate: boolean;
    public locationListItemUpdate: boolean;
    public locationListItemDelete: boolean;
    public locationListCreate: boolean;
    public locationListUpdate: boolean;
    public locationListDelete: boolean;
    public locationListReminderCreate: boolean;
    public locationListReminderDelete: boolean;
    public locationListReminderRead: boolean;
    public locationListReminderUpdate: boolean;
    public locationCartRead: boolean;
    public locationCartAdd: boolean;
    public locationCartUpdate: boolean;
    public locationCartDelete: boolean;
    public locationOrderCreate: boolean;
    public locationOrderRequest: boolean;
    public locationRead: boolean;
    public locationCreate: boolean;
    public locationUpdate: boolean;
    public locationDelete: boolean;
    public locationTeamRead: boolean;
    public locationTeamMemberAdd: boolean;
    public locationTeamMemberDelete: boolean;
    public locationTeamMemberRoleUpdate: boolean;
    public locationContactSupplier: boolean;
    public locationSupplierRead: boolean;
    public locationSupplierCreate: boolean;
    public locationSupplierDelete: boolean;
    public meRead: boolean;
    public meUpdate: boolean;
    public meDelete: boolean;

    constructor(params?: Partial<Record<PERMISSION_KEY, boolean>>) {
        this.locationOrderHistoryRead = !!params?.locationOrderHistoryRead
        this.locationListRead = !!params?.locationListRead
        this.locationListItemRead = !!params?.locationListItemRead
        this.locationListItemCreate = !!params?.locationListItemCreate
        this.locationListItemUpdate = !!params?.locationListItemUpdate
        this.locationListItemDelete = !!params?.locationListItemDelete
        this.locationListCreate = !!params?.locationListCreate
        this.locationListUpdate = !!params?.locationListUpdate
        this.locationListDelete = !!params?.locationListDelete
        this.locationListReminderCreate = !!params?.locationListReminderCreate
        this.locationListReminderDelete = !!params?.locationListReminderDelete
        this.locationListReminderRead = !!params?.locationListReminderRead
        this.locationListReminderUpdate = !!params?.locationListReminderUpdate
        this.locationCartRead = !!params?.locationCartRead
        this.locationCartAdd = !!params?.locationCartAdd
        this.locationCartUpdate = !!params?.locationCartUpdate
        this.locationCartDelete = !!params?.locationCartDelete
        this.locationOrderCreate = !!params?.locationOrderCreate
        this.locationOrderRequest = !!params?.locationOrderRequest
        this.locationRead = !!params?.locationRead
        this.locationCreate = !!params?.locationCreate
        this.locationUpdate = !!params?.locationUpdate
        this.locationDelete = !!params?.locationDelete
        this.locationTeamRead = !!params?.locationTeamRead
        this.locationTeamMemberAdd = !!params?.locationTeamMemberAdd
        this.locationTeamMemberDelete = !!params?.locationTeamMemberDelete
        this.locationTeamMemberRoleUpdate = !!params?.locationTeamMemberRoleUpdate
        this.locationContactSupplier = !!params?.locationContactSupplier
        this.locationSupplierRead = !!params?.locationSupplierRead
        this.locationSupplierCreate = !!params?.locationSupplierCreate
        this.locationSupplierDelete = !!params?.locationSupplierDelete
        this.meRead = !!params?.meRead
        this.meUpdate = !!params?.meUpdate
        this.meDelete = !!params?.meDelete
    }
}

export {
    RegisterCredentials,
    MeResponse,
    NotificationPerm,
    UserPermissions,
}
